import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { trackEvent } from 'helpers/analytics';
import { contactsApi } from 'services/contacts';
import { useTreeNodeDnD } from './useTreeNodeDnD';

export function useTreeNode({
  item,
  level,
  slug,
  isExpandedLookup,
  toggleFolder,
  parentIndex,
  tree,
  expandFolder,
  moveListToFolder,
  moveFolder,
  updateFolder,
  createFolder,
  deleteSharedList,
  duplicateSharedList,
  deleteFolder,
  selectedListId,
  networkId,
}) {
  const history = useHistory();
  const dispatch = useDispatch();

  const isFolder = item.type === 'folder';
  const isExpanded = isExpandedLookup(item.id);

  const [openDeleteList, setOpenDeleteList] = useState(false);
  const [openDuplicateList, setOpenDuplicateList] = useState(false);
  const [openDeleteFolder, setOpenDeleteFolder] = useState(false);
  const [openRenameFolder, setOpenRenameFolder] = useState(false);
  const [openCreateFolder, setOpenCreateFolder] = useState(false);

  const { rowRef, instruction } = useTreeNodeDnD({
    item,
    level,
    isFolder,
    isExpanded,
    parentIndex,
    tree,
    expandFolder,
    moveListToFolder,
    moveFolder,
    networkId,
  });

  const handleRowClick = useCallback(() => {
    if (isFolder) toggleFolder(item.id);
  }, [isFolder, toggleFolder, item.id]);

  const handleOpenDeleteList = useCallback(() => setOpenDeleteList(true), []);
  const handleCloseDeleteList = useCallback(() => setOpenDeleteList(false), []);

  const handleConfirmDeleteList = useCallback(async () => {
    const { error } = await deleteSharedList({ listId: item.id });
    if (!error) {
      setOpenDeleteList(false);
      dispatch(contactsApi.util.invalidateTags(['contacts-shared-lists']));
      trackEvent('list:delete_list', {
        collectionId: networkId,
        listId: item.id,
        page: 'lists',
        origin: 'sidebar',
        object_type: item.kind,
      });
      toast('List deleted', { icon: 'success', id: 'delete-list-success' });
      if (selectedListId === item.id) {
        history.replace(`/networks/${slug}/lists`);
      }
    } else {
      toast('Error deleting list, please try again later', { icon: 'danger', id: 'delete-list-error' });
    }
  }, [deleteSharedList, dispatch, history, item.id, item.kind, networkId, selectedListId, slug]);

  const handleOpenDuplicateList = useCallback(() => setOpenDuplicateList(true), []);
  const handleCloseDuplicateList = useCallback(() => setOpenDuplicateList(false), []);

  const handleConfirmDuplicateList = useCallback(
    async (list, name) => {
      const { error, data } = await duplicateSharedList({ listId: list.id, name });
      if (!error) {
        setOpenDuplicateList(false);
        dispatch(contactsApi.util.invalidateTags(['contacts-shared-lists']));
        trackEvent('list:duplicate_list', {
          collectionId: networkId,
          listId: item.id,
          newListId: data?.sharedList?.id,
          page: 'lists',
          origin: 'sidebar',
          object_type: item.kind,
        });
        toast('List duplicated', { icon: 'success', id: 'duplicate-list-success' });
        if (data?.sharedList?.id) {
          history.push(`/networks/${slug}/list/${data.sharedList.id}/contacts`);
        }
      } else {
        toast('Error duplicating list, please try again later', { icon: 'danger', id: 'duplicate-list-error' });
      }
    },
    [duplicateSharedList, dispatch, history, item.id, item.kind, networkId, slug],
  );

  const handleOpenDeleteFolder = useCallback(() => setOpenDeleteFolder(true), []);
  const handleCloseDeleteFolder = useCallback(() => setOpenDeleteFolder(false), []);

  const handleConfirmDeleteFolder = useCallback(async () => {
    const { error } = await deleteFolder({ folderId: item.id, networkId });
    if (!error) {
      setOpenDeleteFolder(false);
      dispatch(contactsApi.util.invalidateTags(['contacts-shared-lists']));
      trackEvent('lists:delete_folder', {
        collectionId: networkId,
        folderId: item.id,
        page: 'lists',
        origin: 'sidebar',
        hasContents: item.children?.length > 0,
      });
      // If the currently selected list is inside this folder, navigate out
      if (selectedListId != null) {
        let current = parentIndex[`list:${selectedListId}`]?.parentId ?? null;
        while (current != null) {
          if (current === item.id) {
            history.replace(`/networks/${slug}/lists`);
            break;
          }
          current = parentIndex[`folder:${current}`]?.parentId ?? null;
        }
      }
    }
  }, [deleteFolder, dispatch, history, item.children, item.id, networkId, parentIndex, selectedListId, slug]);

  const handleOpenRenameFolder = useCallback(() => setOpenRenameFolder(true), []);
  const handleCloseRenameFolder = useCallback(() => setOpenRenameFolder(false), []);

  const handleSubmitRenameFolder = useCallback(
    async (newName) => {
      const { error } = await updateFolder({ folderId: item.id, name: newName, networkId });
      if (!error) {
        setOpenRenameFolder(false);
        dispatch(contactsApi.util.invalidateTags(['contacts-shared-lists']));
        trackEvent('lists:rename_folder', {
          collectionId: networkId,
          folderId: item.id,
          fromName: item.name,
          toName: newName,
          page: 'lists',
          origin: 'sidebar',
        });
      }
    },
    [dispatch, item.id, item.name, networkId, updateFolder],
  );

  const handleOpenCreateFolder = useCallback(() => setOpenCreateFolder(true), []);
  const handleCloseCreateFolder = useCallback(() => setOpenCreateFolder(false), []);

  const handleSubmitCreateFolder = useCallback(
    async (name) => {
      const parentId = item.id;
      const { error, data } = await createFolder({ name, parentId, networkId });
      if (!error) {
        setOpenCreateFolder(false);
        dispatch(contactsApi.util.invalidateTags(['contacts-shared-lists']));
        trackEvent('lists:create_folder', {
          collectionId: networkId,
          folderId: data?.id,
          name,
          parentId,
          page: 'lists',
          origin: 'sidebar',
        });
      }
      return { error, data };
    },
    [createFolder, dispatch, item.id, networkId],
  );

  const handleCreateListInFolder = useCallback(() => {
    trackEvent('lists:create_list_click', {
      collectionId: networkId,
      page: 'lists',
      origin: 'sidebar',
    });
    expandFolder(item.id);
    history.push({ pathname: `/networks/${slug}/lists/create`, state: { folderId: item.id } });
  }, [expandFolder, history, item.id, networkId, slug]);

  return {
    // derived
    isFolder,
    isExpanded,
    rowRef,
    instruction,
    // ui state
    openDeleteList,
    openDuplicateList,
    openDeleteFolder,
    openRenameFolder,
    openCreateFolder,
    // handlers
    handleRowClick,
    handleOpenDeleteList,
    handleCloseDeleteList,
    handleConfirmDeleteList,
    handleOpenDuplicateList,
    handleCloseDuplicateList,
    handleConfirmDuplicateList,
    handleOpenDeleteFolder,
    handleCloseDeleteFolder,
    handleConfirmDeleteFolder,
    handleOpenRenameFolder,
    handleCloseRenameFolder,
    handleSubmitRenameFolder,
    handleOpenCreateFolder,
    handleCloseCreateFolder,
    handleSubmitCreateFolder,
    handleCreateListInFolder,
  };
}

export default useTreeNode;
